import { useCallback, useMemo, useState } from "react";
import { Search } from "tabler-icons-react";
import {
  ScrollArea,
  Group,
  Text,
  TextInput,
  Button,
  Pagination,
  Box,
  Paper,
  useMantineTheme,
  Badge,
} from "@mantine/core";
import { DateRangePicker } from "@mantine/dates";

import "dayjs/locale/zh-tw";
import { INITIAL_PAGINATION_RESPONSE, useLogsQuery } from "services/api";

import Th from "features/backstage/_shared/Th";
import useTotalPages from "features/backstage/_shared/useTotalPages";
import Table from "features/backstage/_shared/Table";
import getTodayStartEnd from "utils/getTodayStartEnd";

const List = () => {
  const theme = useMantineTheme();

  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<string>("date");
  const [isDescending, setIsDescending] = useState(true);

  const [search, setSearch] = useState<string>();
  const [searchText, setSearchText] = useState<string>("");

  const [duration, setDuration] = useState(getTodayStartEnd());

  const { start, end } = useMemo(() => {
    const [dStart, dEnd] = duration;
    const targetEnd = dEnd && new Date(dEnd.setHours(23, 59, 59, 99));

    return { start: dStart?.toISOString(), end: targetEnd?.toISOString() };
  }, [duration]);

  const { data: logsListData = INITIAL_PAGINATION_RESPONSE } = useLogsQuery(
    { start, end, page, search, sort: isDescending ? `-${sort}` : sort },
    { skip: !start || !end }
  );

  const totalPages = useTotalPages(logsListData.totalCount);

  const handleSort = (sortBy: string) => () => {
    setPage(1);

    if (sortBy === sort) {
      setIsDescending((prev) => !prev);
      return;
    }

    setSort(sortBy);
    setIsDescending(false);
  };

  const getLogLevelColor = useCallback((level: string) => {
    switch (level) {
      case "DEBUG":
      case "INFO":
        return "cyan";

      case "WARN":
        return "orange";

      case "ERROR":
      case "FATAL":
        return "red";

      default:
        return "gray";
    }
  }, []);

  const rows = logsListData.items.map((row) => (
    <tr key={row.id}>
      <td>
        {new Date(row.date).toLocaleString()}
        <br />
      </td>
      <td>
        <Badge color={getLogLevelColor(row.level)}>{row.level}</Badge>
      </td>
      <td>{row.user}</td>
      <td>{row.message}</td>
    </tr>
  ));

  return (
    <Box py={8} px={36}>
      <Group position="apart">
        <h2>系統操作日誌</h2>
      </Group>
      <Paper
        px={16}
        pt={20}
        pb={8}
        mt={16}
        radius={"lg"}
        shadow={theme.shadows.lg}
      >
        <ScrollArea>
          <Group mb={16}>
            <DateRangePicker
              locale="zh-tw"
              placeholder="選擇開始與結束時間"
              value={duration}
              onChange={(values) => {
                setPage(1);
                setDuration(values);
              }}
              style={{
                flex: "30% 0 0",
              }}
              withinPortal
            />

            <form
              style={{
                flex: "auto 1 1",
              }}
              onSubmit={(e) => {
                e.preventDefault();
                setPage(1);
                setSearch(searchText);
              }}
            >
              <Group>
                <TextInput
                  placeholder="輸入用戶名稱或訊息關鍵字進行搜尋"
                  icon={<Search size={14} />}
                  style={{
                    flex: "55% 0 0",
                  }}
                  onChange={({ target: { value } }) => setSearchText(value)}
                />
                <Button color="cyan" type="submit">
                  搜尋
                </Button>
              </Group>
            </form>
          </Group>
          <Table>
            <thead>
              <tr>
                <Th
                  sorted={sort === "date"}
                  reversed={isDescending}
                  onSort={handleSort("date")}
                  cellWidth="20%"
                >
                  時間
                </Th>
                <Th
                  sorted={sort === "level"}
                  reversed={isDescending}
                  onSort={handleSort("level")}
                  cellWidth="15%"
                >
                  層級
                </Th>
                <Th
                  sorted={sort === "user"}
                  reversed={isDescending}
                  onSort={handleSort("user")}
                  cellWidth="15%"
                >
                  用戶名稱
                </Th>
                <Th
                  sorted={sort === "message"}
                  reversed={isDescending}
                  onSort={handleSort("message")}
                  cellWidth="50%"
                >
                  訊息
                </Th>
              </tr>
            </thead>
            <tbody>
              {rows.length > 0 ? (
                rows
              ) : (
                <tr>
                  <td colSpan={4}>
                    <Text weight={500} align="center">
                      沒有找到相關資料
                    </Text>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </ScrollArea>
        <Pagination
          pt={24}
          pb={16}
          total={totalPages}
          page={page}
          color="cyan"
          onChange={setPage}
        />
      </Paper>
    </Box>
  );
};
export default List;
